import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Lightning,
  Sun,
  ChartLineUp,
  CheckCircle,
  ShieldCheck,
  Sparkle,
  ArrowRight,
  HouseLine,
  BatteryCharging,
  CurrencyDollar,
  Clock,
  TrendUp,
  Wrench,
  Bank,
  Lightbulb,
  Wind,
  Warning,
} from "@phosphor-icons/react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import RateBriefingCard from "../components/RateBriefingCard";
import { trackInitiateCheckout } from "../lib/pixel";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8962d87f-5340-48e7-814b-3b48c07b8527/images/e5d46d003a0159d0222c13f2b08d5bce9077fe257ab339fc96f19d7223df3aa5.png";
const PANELS = "https://images.unsplash.com/photo-1694327671725-e2a81cda3436?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxzb2xhciUyMHBhbmVscyUyMHJvb2Z8ZW58MHx8fHwxNzc3Mzg5ODc2fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Lightning, title: "AI Bill Analysis", body: "Snap or upload any HECO/MECO/HELCO/KIUC bill — Claude vision pulls every kWh, rate, and TOU charge in seconds." },
  { icon: Lightbulb, title: "Tips for Everyone", body: "Renters and owners both get personalized energy-saving moves — appliance shifts, TOU timing, behavioral wins." },
  { icon: Clock, title: "TOU Tariff Coaching", body: "Hawaii's Time-of-Use rates spike 5-9pm. We show you exactly when to run AC, laundry, EV charging." },
  { icon: Bank, title: "$0 Down Financing", body: "Qualify for equity funding via Unlock, conventional solar/home-improvement loans, or HVAC partner programs." },
  { icon: Sun, title: "Lock In Lower Power", body: "When solar makes sense, we right-size at $7/W installed — many qualify for 80-90% savings, no money out." },
  { icon: TrendUp, title: "Beat 4.5% Rate Hikes", body: "HECO rates climb every year. We model 25 years of escalation against locked-in solar production." },
];

const steps = [
  { n: "01", title: "Upload your bill", body: "PDF or photo from any island utility. Owners or renters welcome." },
  { n: "02", title: "Quick home profile", body: "Square footage, AC, EV, future plans. Tells us how you actually use power." },
  { n: "03", title: "Get your full plan", body: "Tips you can do today + financing/solar options if you qualify." },
  { n: "04", title: "Pick your path", body: "Free site visit, financing application, HVAC quote, or just keep the report." },
];

const financingOptions = [
  {
    icon: Bank,
    title: "Equity Funding",
    partner: "Powered by Unlock",
    body: "Tap your home equity without a loan or monthly payment. Use it for solar, a new AC unit, a new roof, or whole-home electrification.",
    highlight: "$0 monthly payment",
  },
  {
    icon: Sun,
    title: "Conventional Solar Loans",
    partner: "Multiple lenders",
    body: "Low-interest, long-term financing tailored to solar + battery. Many homeowners qualify with payments lower than their current bill.",
    highlight: "From 5.99% APR",
  },
  {
    icon: Wrench,
    title: "HVAC Partner Network",
    partner: "Heat pumps · Mini-splits",
    body: "Vetted local installers offering rebates, financing, and same-week installs on efficient HVAC systems and water heaters.",
    highlight: "Up to $8k rebates",
  },
  {
    icon: HouseLine,
    title: "Home Improvement Lending",
    partner: "Roofing · Windows · Insulation",
    body: "Bundle a new roof, attic insulation, or efficient windows alongside your solar plan with a single financing package.",
    highlight: "One application",
  },
];

const urgencyPoints = [
  {
    icon: TrendUp,
    title: "HECO rates jumped 4.5% last year — and the year before",
    body: "Hawaii has the highest electricity rates in the nation. Every year you wait costs the average household $300-500 more.",
  },
  {
    icon: Clock,
    title: "Time-of-Use is here. 5-9pm power is now ~50% more expensive",
    body: "Most homeowners don't realize they're getting hit twice — once by usage, once by peak hours. We show you exactly when and how.",
  },
  {
    icon: Warning,
    title: "Federal solar tax credit was just repealed",
    body: "The 30% residential federal credit ended for systems placed in service after Dec 31, 2025. Maui Starpower currently offers a 35% Tax Equity Offset on a limited-time waitlist — that gap is closing fast.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <Header variant="glass" />

      {/* HERO */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-amber-50/50" />
          <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-amber-200/40 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm"
            >
              <Sparkle size={14} weight="fill" className="text-amber-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-700">
                Pilot in Hawaii &middot; Free for everyone
              </span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-950 leading-[1.05]"
            >
              Beat <span className="gradient-text-tech">rising bills.</span>
              <br />
              <span className="gradient-text-solar">Lock in lower power.</span>
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-6 text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl"
            >
              Upload your electric bill. Our AI builds a personalized savings plan with the
              best moves you can make to fight rising rates — many qualify for{" "}
              <span className="font-semibold text-slate-900">80-90% savings with $0 out of pocket</span>.
              Owners, renters, everyone gets tips. See your potential in under 90 seconds.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/analyze"
                data-testid="hero-analyze-btn"
                onClick={trackInitiateCheckout}
                className="btn-primary inline-flex items-center justify-center gap-2 group"
              >
                Analyze My Bill — Free
                <ArrowRight size={20} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#why-now"
                data-testid="hero-howitworks-btn"
                className="btn-secondary inline-flex items-center justify-center"
              >
                Why act now
              </a>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-4 text-sm">
              <div className="flex items-center gap-2 text-slate-700">
                <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                <span>No credit card</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                <span>Renters welcome</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <CheckCircle size={18} weight="fill" className="text-emerald-500" />
                <span>Instant results</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-900/10">
              <img src={HERO} alt="Hawaii solar home" className="w-full h-[480px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-md rounded-2xl p-5 border border-white/40">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                    <Sun size={24} weight="fill" className="text-white" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
                      Avg. qualified homeowner
                    </div>
                    <div className="font-display text-2xl font-bold text-slate-900">
                      80-90% savings
                      <span className="text-sm font-medium text-slate-500"> &middot; $0 down</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute -top-4 -left-4 bg-white rounded-2xl p-4 shadow-xl border border-slate-100 hidden md:block">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} weight="fill" className="text-sky-500" />
                <span className="text-sm font-semibold text-slate-900">Maui Starpower</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Locally owned. Licensed.</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHY NOW — URGENCY / EDUCATION */}
      <section id="why-now" className="py-24 md:py-32 bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-red-500/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          {/* May 2026 HECO Rate Briefing - leading urgency block */}
          <div className="mb-16">
            <RateBriefingCard variant="dark" />
          </div>

          <div className="max-w-3xl mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">
              Why act now
            </span>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mt-4">
              Every month you wait,
              <br /> the math gets worse.
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Hawaii's energy economics are shifting under your feet — Time-of-Use rates,
              compounding rate hikes, and a sunset on tax credits. Most homeowners are paying
              for these changes without ever seeing them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {urgencyPoints.map((point, i) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors"
                data-testid={`urgency-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center mb-5">
                  <point.icon size={24} weight="duotone" className="text-amber-400" />
                </div>
                <h3 className="font-display text-xl font-semibold text-white mb-3 leading-snug">
                  {point.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">{point.body}</p>
              </motion.div>
            ))}
          </div>

          {/* TOU peak callout */}
          <div className="mt-12 rounded-3xl bg-gradient-to-br from-amber-500/10 to-red-500/10 border border-amber-500/20 p-8 md:p-10">
            <div className="grid md:grid-cols-5 gap-8 items-center">
              <div className="md:col-span-2">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-400 font-bold mb-3">
                  TOU Tariff Reality Check
                </div>
                <div className="font-display text-3xl md:text-4xl font-bold text-white leading-tight">
                  Running AC at 6pm costs ~50% more than at 11am.
                </div>
              </div>
              <div className="md:col-span-3">
                <p className="text-slate-300 leading-relaxed mb-4">
                  Most Hawaiian Electric residential customers are now on or being moved to
                  Time-of-Use schedules. The 5-9pm "peak window" charges premium rates —
                  exactly when most families come home and crank the AC.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  Our analyzer maps your usage against TOU windows and shows the simple
                  shifts that can knock 15-25% off your bill — even before solar enters
                  the conversation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES — WHAT YOU GET */}
      <section className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600">
              Built for Hawaii — works for everyone
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-950 mt-4">
              Free tools and tips first.
              <br /> Big savings for those who qualify.
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              Not every household is right for solar — and that's fine. Our AI gives renters
              and owners alike the moves that actually move the needle on Hawaii's most
              expensive utility bills in the country.
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="card-clean p-8"
                data-testid={`feature-card-${i}`}
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center mb-5">
                  <f.icon size={24} weight="duotone" className="text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 mb-2">
                  {f.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-32">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-600">
                The Process
              </span>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-950 mt-4">
                90 seconds to your savings plan.
              </h2>
              <p className="mt-6 text-lg text-slate-600">
                No salesperson. No spam calls. Just real numbers and real moves based on
                your actual usage.
              </p>
              <Link
                to="/analyze"
                data-testid="how-it-works-cta"
                className="btn-primary inline-flex items-center gap-2 mt-8"
              >
                Start now — it's free <ArrowRight size={18} weight="bold" />
              </Link>
            </div>

            <div className="lg:col-span-7 space-y-4">
              {steps.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="card-clean p-8 flex gap-6"
                >
                  <div className="font-display text-5xl font-bold text-sky-500/30">{s.n}</div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-slate-900">{s.title}</h3>
                    <p className="text-slate-600 mt-1">{s.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINANCING / BACK-END OFFERS */}
      <section id="financing" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">
              Your savings paths
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-950 mt-4">
              No money? No problem.
              <br /> We have a path for that too.
            </h2>
            <p className="mt-6 text-lg text-slate-600">
              The biggest reason families don't upgrade isn't price — it's <em>cash flow</em>.
              We've stacked four financing partners so you can fund energy upgrades, replace an
              AC, get a new roof, or go solar without writing a check.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {financingOptions.map((opt, i) => (
              <motion.div
                key={opt.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="card-clean p-8 flex gap-5"
                data-testid={`financing-card-${i}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-600 flex-shrink-0 flex items-center justify-center">
                  <opt.icon size={28} weight="duotone" className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2 gap-3">
                    <h3 className="font-display text-xl font-semibold text-slate-900">
                      {opt.title}
                    </h3>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full whitespace-nowrap">
                      {opt.highlight}
                    </span>
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
                    {opt.partner}
                  </div>
                  <p className="text-slate-600 leading-relaxed">{opt.body}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-slate-500 text-sm">
              We don't lend directly — we connect you to the option that fits your situation.
              Pre-qualification takes minutes and won't hit your credit.
            </p>
          </div>
        </div>
      </section>

      {/* PRICING / SOLAR (BACKEND DETAIL) */}
      <section className="py-24 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={PANELS} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/95 to-slate-950/80" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">
              When solar is the answer
            </span>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white mt-4">
              $7 per watt installed.
              <br /> 35% Tax-Equity offset (waitlist).
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              The 30% federal residential solar credit was repealed by the 2026 reconciliation
              bill. Maui Starpower is currently offering a <strong className="text-amber-300">35% Tax Equity
              Offset</strong> on a limited-time waitlist for qualified systems. Stacked with
              Hawaii's $5k state credit and our $7/W installed pricing, qualified homes can
              still hit 80-90% lifetime savings — but spots are limited.
            </p>
            <Link
              to="/analyze"
              data-testid="pricing-cta"
              className="btn-primary inline-flex items-center gap-2 mt-8"
            >
              See if I qualify <ArrowRight size={18} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Avg system size", value: "9.5 kW" },
              { label: "Avg battery", value: "13 kWh" },
              { label: "Tax equity offset", value: "35%" },
              { label: "HI state credit", value: "$5,000" },
              { label: "Possible savings", value: "80-90%" },
              { label: "Out of pocket", value: "$0 down" },
            ].map((stat) => (
              <div key={stat.label} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                  {stat.label}
                </div>
                <div className="font-display text-3xl font-bold text-white mt-2">{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-4xl sm:text-6xl font-bold tracking-tight text-slate-950 leading-tight">
            Your bill is <span className="gradient-text-tech">information</span>.
            <br /> Let it show you the way out.
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto">
            Free for everyone. Tips for renters. Financing for owners. Solar for those who
            qualify. Get your plan in 90 seconds.
          </p>
          <Link
            to="/analyze"
            data-testid="final-cta-btn"
            onClick={trackInitiateCheckout}
            className="btn-primary inline-flex items-center gap-2 mt-10"
          >
            Analyze My Bill <ArrowRight size={20} weight="bold" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
